import { Col, Input, Modal, notification, Row } from 'antd'
import { CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import styles from '../additionDrug.module.css'
import { RootDispatch } from '../../../../../app/store'
import {
  getMaintainCustomListAsync,
  removeMaintainCustomAsync,
  saveMaintainCustomAsync,
} from '../addtionDrugSlice'

const types = [
  { type: 0, title: '养护措施', placeholder: '请输入养护措施' },
  { type: 1, title: '质量状况', placeholder: '请输入质量状况' },
  { type: 2, title: '养护结果', placeholder: '请输入养护结果' },
]

const renderItemText = (item: any) => {
  if (item == null) return ''
  if (typeof item === 'string' || typeof item === 'number') return String(item)
  return item?.content ?? item?.name ?? ''
}

const CustomTypeInput = (props: {
  type: number
  placeholder: string
  onSaved: () => void
  onRefresh: () => void
  autoFocus?: boolean
  focusTick?: number
  items: any[]
}) => {
  const dispatch = useDispatch<RootDispatch>()
  const [value, setValue] = useState('')
  const inputRef = useRef<any>(null)

  useEffect(() => {
    if (props.focusTick) {
      inputRef.current?.focus()
    }
  }, [props.focusTick])

  const doSave = () => {
    const v = value?.trim()
    if (!v) return
    if (props.items.some((item) => String(renderItemText(item) ?? '').trim() === v)) {
      setValue('')
      notification.info({ message: '内容已存在' })
      props.onRefresh()
      inputRef.current?.focus()
      return
    }
    dispatch(saveMaintainCustomAsync({ content: v, type: props.type }))
      .then(unwrapResult)
      .then(() => {
        notification.success({ message: '保存成功' })
        props.onSaved()
      })
      .catch(() => { return })
    setValue('')
  }

  return (
    <Input
      ref={inputRef}
      autoFocus={props.autoFocus}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onPressEnter={() => {
        if (!value?.trim()) {
          notification.info({ message: props.placeholder })
          return
        }
        doSave()
      }}
      onBlur={() => {
        doSave()
      }}
      maxLength={15}
      placeholder={props.placeholder}
    />
  )
}

const EditItem = (props: {
  item: any
  type: number
  placeholder: string
  onSaved: () => void
  onRemoved: () => void
  onRefresh: () => void
  items: any[]
}) => {
  const dispatch = useDispatch<RootDispatch>()
  const [value, setValue] = useState(renderItemText(props.item))
  const savingRef = useRef(false)
  const inputRef = useRef<any>(null)

  const doSave = () => {
    if (savingRef.current) return
    const v = value?.trim()
    if (!v) {
      notification.info({ message: props.placeholder })
      return
    }
    if (
      props.items.some(
        (item) => item?.id !== props.item?.id && String(renderItemText(item) ?? '').trim() === v,
      )
    ) {
      setValue('')
      notification.info({ message: '内容已存在' })
      props.onRefresh()
      inputRef.current?.focus()
      return
    }
    savingRef.current = true
    //
    dispatch(saveMaintainCustomAsync({ content: v, id: props.item?.id, type: props.type }))
      .then(unwrapResult)
      .then(() => {
        notification.success({ message: '保存成功' })
        props.onSaved()
      })
      .catch(() => { return })
      .finally(() => {
        savingRef.current = false
      })
  }

  const doRemove = () => {
    Modal.confirm({
      title: '是否确认删除?',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        dispatch(removeMaintainCustomAsync({ id: props.item?.id }))
          .then(unwrapResult)
          .then(() => {
            notification.success({ message: '删除成功' })
            props.onRemoved()
          })
          .catch(() => { return })
      },
    })
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={doSave}
        onBlur={doSave}
        maxLength={15}
        className={styles.selectInput}
      />
      <CheckCircleOutlined
        style={{ fontSize: 19, marginLeft: 10, color: '#027AFF', cursor: 'pointer' }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={doSave}
      />
      <DeleteOutlined
        style={{ fontSize: 19, marginLeft: 10, color: 'red', cursor: 'pointer' }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={doRemove}
      />
    </div>
  )
}

export const SettingModal = (props?: {
  visible?: boolean,
  onOk: () => void,
  onCancel?: () => void,
}): ReactElement => {
  const dispatch = useDispatch<RootDispatch>()
  const [groupData, setGroupData] = useState<Record<number, any[]>>({})
  const [editingId, setEditingId] = useState<any>()
  const [focusTarget, setFocusTarget] = useState<{ type: number; tick: number }>({ type: 0, tick: 0 })

  const focusInput = (type: number) => {
    setFocusTarget((prev) => ({ type, tick: prev.tick + 1 }))
  }

  const loadList = () => {
    dispatch(getMaintainCustomListAsync())
      .then(unwrapResult)
      .then((res: any) => {
        const list = Array.isArray(res) ? res : (res?.records ?? res?.data ?? [])
        const grouped: Record<number, any[]> = { 0: [], 1: [], 2: [] }
        list.forEach((item: any) => {
          const t = Number(item?.type)
          if (t === 0 || t === 1 || t === 2) {
            grouped[t].push(item)
          }
        })
        setGroupData(grouped)
      })
      .catch(() => { return })
  }

  useEffect(() => {
    if (props?.visible) {
      loadList()
    }
  }, [props?.visible])

  return (
    <Modal
      title='设置'
      maskClosable={false}
      visible={props?.visible}
      width={900}
      footer={null}
      destroyOnClose
      bodyStyle={{ padding: '16px' }}
      onOk={() => {
        setGroupData({})
        setEditingId(undefined)
        props?.onOk && props?.onOk()
      }}
      onCancel={() => {
        setGroupData({})
        setEditingId(undefined)
        props?.onCancel && props?.onCancel()
      }}
    >
      <div style={{ height: '500px', overflowX: 'hidden', overflowY: 'auto' }}>
        {types.map((t) => (
          <div key={t.type} style={{ marginBottom: 10 }}>
            <Row className={styles.title}>{t.title}</Row>
            <Row gutter={10} style={{ marginBottom: 10 }}>
              {(groupData[t.type] || []).map((item: any, idx: number) => (
                <Col
                  key={item?.id ?? idx}
                  style={{ width: '19%', marginRight: '1%', marginBottom: 10 }}
                >
                  {editingId === item?.id ? (
                    <EditItem
                      item={item}
                      type={t.type}
                      placeholder={t.placeholder}
                      items={groupData[t.type] || []}
                      onRefresh={loadList}
                      onSaved={() => {
                        setEditingId(undefined)
                        loadList()
                        focusInput(t.type)
                      }}
                      onRemoved={() => {
                        setEditingId(undefined)
                        loadList()
                      }}
                    />
                  ) : (
                    <div
                      className={styles.btnStyle}
                      onClick={() => setEditingId(item?.id)}
                    >
                      {renderItemText(item)}
                    </div>
                  )}
                </Col>
              ))}
              <Col style={{ width: '19%', marginRight: '1%', marginBottom: 10 }}>
                <CustomTypeInput
                  type={t.type}
                  placeholder={t.placeholder}
                  onSaved={() => {
                    loadList()
                    focusInput(t.type)
                  }}
                  onRefresh={loadList}
                  autoFocus={t.type === 0}
                  focusTick={focusTarget.type === t.type ? focusTarget.tick : undefined}
                  items={groupData[t.type] || []}
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>
    </Modal>
  )
}
